import {
  FormControl,
  InputLabel,
  MenuItem,
  Switch,
  Card,
  Divider,
  Collapse,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useRef, useState, useEffect, useCallback } from "react";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { Autocomplete, Chip } from "@mui/material";
import {
  updatePlace,
  getPlaceById,
  setSelectedPlace,
  setSinglePlace,
} from "store/slice/places/placeSlice";
import MDSnackbar from "components/MDSnackbar";
import MDDropDown from "components/MDDropDown";

// ─── Validation Schema ────────────────────────────────────────────────────────

const validationSchema = Yup.object().shape({
  apiKey: Yup.string().when(
    ["enabled", "enforecementType", "sendToParkpliant"],
    {
      is: (enabled, enforecementType, sendToParkpliant) =>
        enabled &&
        (enforecementType === "prkpliant" || enforecementType === "tagger") &&
        sendToParkpliant,
      then: () => Yup.string().required("Api Key is required!"),
      otherwise: () => Yup.string().notRequired(),
    }
  ),
  apiPassword: Yup.string().when(
    ["enabled", "enforecementType", "sendToParkpliant"],
    {
      is: (enabled, enforecementType, sendToParkpliant) =>
        enabled &&
        (enforecementType === "prkpliant" || enforecementType === "tagger") &&
        sendToParkpliant,
      then: () => Yup.string().required("Api Password is required!"),
      otherwise: () => Yup.string().notRequired(),
    }
  ),
  facilityId: Yup.string().when(["enableSpotHero"], {
    is: (enableSpotHero) => enableSpotHero,
    then: () => Yup.string().required("Facility ID is required!"),
    otherwise: () => Yup.string().notRequired(),
  }),
  sendToParkpliant: Yup.boolean(),
  emailNotification: Yup.string().when("enableEmail", {
    is: (enableEmail) => enableEmail,
    then: () =>
      Yup.string().email("Invalid email format").required("Email is required!"),
    otherwise: () => Yup.string().notRequired(),
  }),
  ccEmailNotification: Yup.array().notRequired(),
});

// ─── Default / Initial Values ─────────────────────────────────────────────────

const DEFAULT_VALUES = {
  enabled: false,
  apiKey: "",
  apiPassword: "",
  facilityId: "",
  slackNotification: false,
  enableSpotHero: false,
  enableT2: false,
  sendToParkpliant: false,
  enforecementType: "prkpliant",
  enableEmail: false,
  emailNotification: "",
  ccEmailNotification: [],
};

// ─── Helper: map enforcement settings object → formik field map ───────────────

function settingsToFields(s = {}) {
  return {
    enabled: s.enabled ?? false,
    apiKey: s.apiKey ?? "",
    apiPassword: s.apiPassword ?? "",
    facilityId: s.facilityId ?? "",
    enforecementType: s.enforecementType ?? "prkpliant",
    slackNotification: s.slackNotification ?? false,
    enableSpotHero: s.enableSpotHero ?? false,
    enableT2: s.enableT2 ?? false,
    sendToParkpliant: s.sendToParkpliant ?? false,
    enableEmail: s.enableEmail ?? false,
    emailNotification: s.emailNotification ?? "",
    ccEmailNotification: s.ccEmailNotification ?? [],
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Consistent toggle card used for every feature toggle */
function ToggleCard({ label, checked, onChange, disabled, children }) {
  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        mb: 2,
        borderRadius: "12px",
        border: "1.5px solid",
        borderColor:  "divider",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: checked ? "0 0 0 3px rgba(66,133,244,0.08)" : "none",
        overflow: "hidden",
      }}
    >
      <MDBox
        px={2}
        py={1.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: checked ? "rgba(66,133,244,0.04)" : "transparent",
          transition: "background-color 0.2s ease",
        }}
      >
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{ color: disabled ? "text.disabled" : "text.primary" }}
        >
          {label}
        </MDTypography>
        <Switch checked={checked} onChange={onChange} disabled={disabled} />
      </MDBox>

      <Collapse in={checked && !!children}>
        <Divider sx={{ my: 0 }} />
        <MDBox px={2} py={2}>
          {children}
        </MDBox>
      </Collapse>
    </Card>
  );
}

/** Section wrapper with a left-accent bar */
function Section({ title, subtitle, children, icon }) {
  return (
    <MDBox
      sx={{
        borderRadius: "14px",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        mb: 3,
      }}
    >
      <MDBox
        px={3}
        py={2}
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          background:
            "linear-gradient(135deg, rgba(66,133,244,0.06) 0%, rgba(66,133,244,0.02) 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {icon && (
          <MDBox
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "1rem",
            }}
          >
            {icon}
          </MDBox>
        )}
        <MDBox>
          <MDTypography variant="h6" fontWeight="bold" lineHeight={1.2}>
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography variant="caption" color="secondary">
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <MDBox px={3} py={2.5}>
        {children}
      </MDBox>
    </MDBox>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ParkPliantSettings() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();

  const placesData = useSelector((state) => state.places?.selectedPlace);
  const singlePlace = useSelector((state) => state.places?.singlePlace);
  const placeId =
    placesData?._id || new URLSearchParams(location?.search).get("placeId");

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
  });

  const closeNotification = useCallback(
    () => setNotification((n) => ({ ...n, show: false })),
    []
  );

  // ── Apply settings object to formik ──────────────────────────────────────
  const applyToFormik = useCallback((settings) => {
    const fields = settingsToFields(settings);
    Object.entries(fields).forEach(([key, val]) =>
      formikRef.current?.setFieldValue(key, val)
    );
  }, []);

  // ── Load place data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (singlePlace?.enforecementSettings) {
      applyToFormik(singlePlace.enforecementSettings);
    } else {
      dispatch(getPlaceById(placeId))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setSinglePlace(res.place));
            applyToFormik(res.place?.enforecementSettings ?? {});
          }
        });
    }
  }, [placeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when singlePlace updates from elsewhere
  useEffect(() => {
    if (singlePlace?.enforecementSettings) {
      applyToFormik(singlePlace.enforecementSettings);
    }
  }, [singlePlace, applyToFormik]);

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    (values) => {
      const enforecementSettings = {
        enabled: values.enabled,
        apiKey: values.apiKey,
        apiPassword: values.apiPassword,
        facilityId: values.facilityId,
        slackNotification: values.slackNotification,
        enableT2: values.enableT2,
        enableSpotHero: values.enableSpotHero,
        sendToParkpliant: values.sendToParkpliant,
        enforecementType: values.enforecementType,
        enableEmail: values.enableEmail,
        emailNotification: values.emailNotification,
        ccEmailNotification: values.ccEmailNotification,
      };

      dispatch(updatePlace({ placeId, type: "UPDATE", enforecementSettings }))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(getPlaceById(placeId))
              .unwrap()
              .then((r) => {
                if (r?.success) {
                  dispatch(setSelectedPlace(r.place));
                  dispatch(setSinglePlace(r.place));
                }
              });
          }
          setNotification({
            color: res?.success ? "success" : "error",
            title: res?.success ? "Success" : "Error",
            content: res?.message || "Settings updated successfully",
            icon: res?.success ? "check" : "warning",
            show: true,
          });
        })
        .catch((err) => {
          setNotification({
            color: "error",
            title: "Error",
            content: err?.message || "Failed to update settings",
            icon: "warning",
            show: true,
          });
        });
    },
    [dispatch, placeId]
  );

  // ── Clear API field errors helper ─────────────────────────────────────────
  const clearApiErrors = (props) => {
    ["apiKey", "apiPassword", "facilityId"].forEach((field) => {
      props.setFieldTouched(field, false);
      props.setFieldError(field, undefined);
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <MDBox
      p={{ xs: 2, md: 4 }}
      sx={{
        bgcolor: "background.paper",
        borderRadius: "18px",
        boxShadow: "0 4px 32px rgba(0,0,0,0.08)",
        // maxWidth: 900,
        // mx: "auto",
      }}
    >
      {/* ── Page Header ── */}
      <MDBox mb={4} display="flex" alignItems="center" gap={2}>
        <MDBox
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: "linear-gradient(135deg, #4285F4, #0F52BA)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 14px rgba(66,133,244,0.35)",
            flexShrink: 0,
            fontSize: "1.4rem",
          }}
        >
          🅿
        </MDBox>
        <MDBox>
          <MDTypography variant="h4" fontWeight="bold" lineHeight={1.2}>
            Enforcement Settings
          </MDTypography>
          <MDTypography variant="body2" color="secondary" mt={0.3}>
            Configure parking enforcement integrations and notifications
          </MDTypography>
        </MDBox>
      </MDBox>

      <Formik
        innerRef={formikRef}
        initialValues={DEFAULT_VALUES}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(props) => {
          const isEnabled = props.values.enabled;

          return (
            <form onSubmit={props.handleSubmit}>
              {/* ── Master Toggle ── */}
              <MDBox
                mb={3}
                p={2.5}
                sx={{
                  borderRadius: "14px",
                  border: "1px solid",
                  borderColor: "divider",
                  background: isEnabled
                    ? "linear-gradient(135deg, rgba(66,133,244,0.08) 0%, rgba(15,82,186,0.04) 100%)"
                    : "transparent",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="bold">
                    Enable Enforcement
                  </MDTypography>
                  <MDTypography variant="caption" color="secondary">
                    {isEnabled
                      ? "Enforcement is active — configure options below"
                      : "Toggle on to activate enforcement features"}
                  </MDTypography>
                </MDBox>
                <Switch
                  checked={isEnabled}
                  onChange={(e) => {
                    props.setFieldValue("enabled", e.target.checked);
                    if (!e.target.checked) {
                      props.setFieldValue("slackNotification", false);
                      props.setFieldValue("enableSpotHero", false);
                      props.setFieldValue("enableT2", false);
                      props.setFieldValue("sendToParkpliant", false);
                      props.setFieldValue("enforecementType", "prkpliant");
                      clearApiErrors(props);
                    }
                  }}
                  sx={{ ml: 2 }}
                />
              </MDBox>

              {/* ── Feature Toggles Section ── */}
              <Section
                title="Feature Toggles"
                subtitle="Enable or disable individual enforcement integrations"
                icon="⚙️"
              >
                <Grid container spacing={2}>
                  {/* Email Notifications */}
                  <Grid item xs={12} sm={6}>
                    <ToggleCard
                      label="Email Notifications"
                      checked={props.values.enableEmail}
                      onChange={(e) =>
                        props.setFieldValue("enableEmail", e.target.checked)
                      }
                      disabled={!isEnabled}
                    >
                      <MDBox display="flex" flexDirection="column" gap={2}>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="secondary"
                          sx={{ textTransform: "uppercase", letterSpacing: 1 }}
                        >
                          Email Configuration
                        </MDTypography>
                        <MDInput
                          fullWidth
                          label="Notification Email"
                          name="emailNotification"
                          value={props.values.emailNotification}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error={
                            props.errors.emailNotification &&
                            props.touched.emailNotification
                          }
                          helperText={
                            props.touched.emailNotification &&
                            props.errors.emailNotification
                          }
                        />
                        <Autocomplete
                          multiple
                          disableClearable
                          options={[]}
                          value={props.values.ccEmailNotification}
                          onChange={(_, newValue) =>
                            props.setFieldValue("ccEmailNotification", newValue)
                          }
                          onBlur={props.handleBlur}
                          freeSolo
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="contained"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <MDInput
                              label="CC Emails"
                              fullWidth
                              {...params}
                              error={
                                !!(
                                  props.errors.ccEmailNotification &&
                                  props.touched.ccEmailNotification
                                )
                              }
                              helperText={
                                props.touched.ccEmailNotification
                                  ? props.errors.ccEmailNotification
                                  : null
                              }
                            />
                          )}
                        />
                      </MDBox>
                    </ToggleCard>
                  </Grid>

                  {/* Slack Notifications */}
                  <Grid item xs={12} sm={6}>
                    <ToggleCard
                      label="Slack Notifications"
                      checked={props.values.slackNotification}
                      onChange={(e) =>
                        props.setFieldValue(
                          "slackNotification",
                          e.target.checked
                        )
                      }
                      disabled={!isEnabled}
                    />
                  </Grid>

                  {/* Enable T2 */}
                  <Grid item xs={12} sm={6}>
                    <ToggleCard
                      label="Enable T2"
                      checked={props.values.enableT2}
                      onChange={(e) =>
                        props.setFieldValue("enableT2", e.target.checked)
                      }
                      disabled={!isEnabled}
                    />
                  </Grid>

                  {/* Spot Hero */}
                  <Grid item xs={12} sm={6}>
                    <ToggleCard
                      label="Enable Spot Hero"
                      checked={props.values.enableSpotHero}
                      onChange={(e) => {
                        props.setFieldValue("enableSpotHero", e.target.checked);
                        if (!e.target.checked) {
                          props.setFieldTouched("facilityId", false);
                          props.setFieldError("facilityId", undefined);
                        }
                      }}
                      disabled={!isEnabled}
                    >
                      <MDInput
                        fullWidth
                        label="Facility ID"
                        name="facilityId"
                        required={
                          props.values.enableSpotHero || props.values.enableT2
                        }
                        value={props.values.facilityId}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        error={
                          props.errors.facilityId && props.touched.facilityId
                        }
                        helperText={
                          props.touched.facilityId && props.errors.facilityId
                        }
                      />
                    </ToggleCard>
                  </Grid>

                  {/* Send to Parkpliant */}
                  <Grid item xs={12}>
                    <ToggleCard
                      label="Send to Parkpliant"
                      checked={props.values.sendToParkpliant}
                      onChange={(e) =>
                        props.setFieldValue(
                          "sendToParkpliant",
                          e.target.checked
                        )
                      }
                      disabled={!isEnabled}
                    >
                      <Grid container spacing={2}>
                        {/* Enforcement Type */}
                        <Grid item xs={12} md={6}>
                          <MDBox>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 1,
                                display: "block",
                                mb: 1.5,
                              }}
                            >
                              Enforcement Type
                            </MDTypography>
                            <FormControl fullWidth>
                              <InputLabel id="enforcement-type-label">
                                Enforcement Type
                              </InputLabel>
                              <MDDropDown
                                labelId="enforcement-type-label"
                                id="enforcement-type"
                                name="enforecementType"
                                value={props.values.enforecementType}
                                label="Enforcement Type"
                                onChange={(_event, value) => {
                                  const newType = value.props.value;
                                  props.setFieldValue(
                                    "enforecementType",
                                    newType
                                  );
                                  if (newType !== "prkpliant") {
                                    clearApiErrors(props);
                                  }
                                }}
                                disabled={!isEnabled}
                              >
                                <MenuItem value="prkpliant">
                                  Park Pliant
                                </MenuItem>
                                <MenuItem value="tagger">Tagger</MenuItem>
                                <MenuItem value="none">None</MenuItem>
                              </MDDropDown>
                            </FormControl>
                          </MDBox>
                        </Grid>

                        {/* API Settings */}
                        <Grid item xs={12} md={6}>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="secondary"
                            sx={{
                              textTransform: "uppercase",
                              letterSpacing: 1,
                              display: "block",
                              mb: 1.5,
                            }}
                          >
                            API Credentials
                          </MDTypography>
                          <MDBox display="flex" flexDirection="column" gap={2}>
                            <MDInput
                              fullWidth
                              label={
                                props.values.enforecementType === "tagger"
                                  ? "Parkpliant API Key"
                                  : "API Key"
                              }
                              name="apiKey"
                              required={
                                isEnabled &&
                                (props.values.enforecementType === "prkpliant" ||
                                  props.values.enforecementType === "tagger")
                              }
                              value={props.values.apiKey}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.errors.apiKey && props.touched.apiKey
                              }
                              helperText={
                                props.touched.apiKey && props.errors.apiKey
                              }
                              disabled={
                                !isEnabled &&
                                (props.values.enforecementType === "prkpliant" ||
                                  props.values.enforecementType === "tagger")
                              }
                            />
                            <MDInput
                              fullWidth
                              label={
                                props.values.enforecementType === "tagger"
                                  ? "Parkpliant API Password"
                                  : "API Password"
                              }
                              name="apiPassword"
                              required={
                                isEnabled &&
                                (props.values.enforecementType === "prkpliant" ||
                                  props.values.enforecementType === "tagger")
                              }
                              value={props.values.apiPassword}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              error={
                                props.errors.apiPassword &&
                                props.touched.apiPassword
                              }
                              helperText={
                                props.touched.apiPassword &&
                                props.errors.apiPassword
                              }
                              disabled={
                                !isEnabled &&
                                (props.values.enforecementType === "prkpliant" ||
                                  props.values.enforecementType === "tagger")
                              }
                            />
                          </MDBox>
                        </Grid>
                      </Grid>
                    </ToggleCard>
                  </Grid>
                </Grid>
              </Section>

              {/* ── Save Button ── */}
              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <MDButton
                  color={sidenavColor}
                  variant="contained"
                  type="submit"
                  size="large"
                  disabled={!props.isValid && props.submitCount > 0}
                  sx={{
                    px: 5,
                    borderRadius: "10px",
                    fontWeight: "bold",
                    letterSpacing: 0.5,
                    boxShadow: "0 4px 14px rgba(66,133,244,0.3)",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(66,133,244,0.45)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Save Settings
                </MDButton>
              </MDBox>
            </form>
          );
        }}
      </Formik>

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={closeNotification}
        bgWhite
      />
    </MDBox>
  );
}

export default ParkPliantSettings;